/*!
 * liveDocs.js — Ctrl+Shift+D: the help page of the cue under the caret
 * © 2026 Rob Canning — GPLv3
 *
 *     synth(freq:220, |amp:0.1)    Ctrl+Shift+D -> the synth() page, in a new tab
 *     a1 > rot|ate(dur:2)          -> rotate()
 *     say(a1, voice:|)             -> say()
 *     (no cue there)               -> the live console's pages
 *
 * The cue is the word under the caret when a call follows it, else the call
 * the caret is inside (the innermost one that has a page: rand() inside a
 * synth() gives synth()). Pages are the Score docs' (/oscilla/docs/) on a
 * score; Oscilla Live's own (useLiveDocs) on live.html.
 */

let base = "/oscilla/docs/";
let home = "oscilive/";            // on a score: live coding in a score

/**
 * Live's page: its own docs, opening on their front page -- next to the
 * page (oscilla.cc/live/docs/), on Oscilla's server (/live/docs/), else
 * oscilla.cc's. Returns where they are.
 */
export async function useLiveDocs() {
  const found = async u => { try { return (await fetch(u, { method: "HEAD" })).ok; } catch { return false; } };
  const near = new URL("docs/", location.href).pathname;
  base = (await found(near)) ? near : (await found("/live/docs/")) ? "/live/docs/" : "https://oscilla.cc/live/docs/";
  home = "";
  return base;
}

// cues with a page of their own, by folder (test/cueNames.test.js: every
// cue the console offers has a page, and every page named here exists)
const CUE_PAGES = ["adc", "cam", "scene", "console", "audio", "audioImpulse", "audioPool", "button", "extent", "fx", "group", "hush",
  "image", "level", "metronome", "midi", "nav", "notation", "osc", "oscCtrl", "page", "pause", "pin", "propagate",
  "random", "resources", "reuse", "say", "speakers", "speed", "station", "stop", "stopwatch", "synth", "text", "url", "video", "viz", "vu"];
const ANIMATION_PAGES = ["color", "draw", "fade", "o2p", "rotate", "scale", "shape", "trans", "traverse", "ui", "zoom"];

// cues found in another page, or under another name
const ELSEWHERE = {
  tempo: "cues/cue_synth/#tempo--tempo",
  hold: "cues/cue_synth/#holding-a-pattern--hold",
  bang: "cues/cue_synth/#rolling-it-again--bang",
  gate: "cues/cue_synth/#letting-go-and-pressing-again--gate",
  seq: "cues/cue_synth/#stopping-the-sequence-after-its-note--seq",
  xfade: "cues/cue_level/",
  solo: "cues/cue_level/#solo--solo",
  style: "cues/cue_text/#style-presets--style",
  screen: "oscilive/visuals/#screen----the-whole-stage",
  scatter: "oscilive/visuals/#scatter----throwing-paths-about",
  grid: "oscilive/visuals/#grid----the-stage-in-cells",
  strobe: "oscilive/visuals/#lines-strobes-barcodes-data",
  scenes: "cues/cue_scene/#sequences----scenes",
  palette: "cues/cue_viz/#palettes",
  scaleXY: "animation/cue_scale/",
  colour: "animation/cue_color/",
  play: "cues/cue_stop/#stop-and-play",
  rewind: "cues/cue_stop/#rewind",
  seek: "cues/cue_stop/#seek",
  leg: "cues/cue_station/#leg",
  metro: "cues/cue_metronome/",
  capture: "cues/cue_resources/",
  still: "session/liveconsole/",
  projector: "session/liveconsole/",
  snapshot: "session/liveconsole/#keeping-the-code",
  wait: "cues/cue_scene/#in-turn----wait",
  versions: "session/liveconsole/#keeping-the-code",
  room: "session/liveconsole/#ensemble-or-jam",
  clock: "oscilive/sync/",
  downbeat: "oscilive/sync/#downbeat",
  latency: "oscilive/sync/#latency",
  map: "animation/cue_shape/#fitting-it-onto-a-surface--map",
  use: "session/livecoding/",
  folder: "oscilive/files/",
};

/** The page of a cue, or null when it has none. */
export function docPath(name) {
  if (!name) return null;
  if (ELSEWHERE[name]) return ELSEWHERE[name];
  if (CUE_PAGES.includes(name)) return `cues/cue_${name}/`;
  if (ANIMATION_PAGES.includes(name)) return `animation/cue_${name}/`;
  return null;
}

/**
 * The cue at `pos` in `text`: the word under the caret when "(" follows
 * it, else the innermost open call before the caret that has a page.
 */
export function cueAt(text, pos) {
  const left = /[A-Za-z_]\w*$/.exec(text.slice(0, pos))?.[0] || "";
  const right = /^\w*/.exec(text.slice(pos))[0];
  const word = left + right;
  if (word && /^\s*\(/.test(text.slice(pos + right.length)) && docPath(word)) return word;
  // the calls still open before the caret, strings skipped
  const stack = [];
  let quote = "";
  const before = text.slice(0, pos);
  for (let i = 0; i < before.length; i++) {
    const ch = before[i];
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (ch === "\n" && !stack.length) continue;
    if (ch === "(") stack.push(/([A-Za-z_]\w*)\s*$/.exec(before.slice(0, i))?.[1] || "");
    else if (ch === ")") stack.pop();
  }
  for (let i = stack.length - 1; i >= 0; i--) if (docPath(stack[i])) return stack[i];
  // the caret on a line's first word with no bracket yet: synth|
  return docPath(word) ? word : null;
}

/** The url of the help for the caret: its cue's page, or the console's. */
export function helpUrl(text, pos) {
  const cue = cueAt(text, pos);
  return base + (docPath(cue) || home);
}
